import { FC, PropsWithChildren, useEffect, useRef, useState } from "react";
import styled, { keyframes, useTheme } from "styled-components";
import { Box, createCSSFromObject } from "../box/Box";
import { ITheme } from "../theme/GlobalTheme";

type Props = PropsWithChildren<{
	shape?: "rect" | "circle" | "line";
	mode?: "auto" | "manual";
	width?: string | number;
	height?: string | number;
	center?: boolean;
	busy?: boolean;
	sx?: React.CSSProperties;
	sxWrap?: React.CSSProperties;
}>;

const skeletonAnimation = keyframes`
    from {
      background-position: -100% 0;
    }
    to {
      background-position: 200% 0;
    }
`;

const BoxAnimated = styled.div<{ $sx?: React.CSSProperties }>`
	overflow: hidden;
	background-repeat: no-repeat;
	/* A light sweep over the theme base colour, so the placeholder reads as "loading", not as an empty box. */
	background-image: linear-gradient(90deg, transparent 0%, rgba(255, 255, 255, 0.6) 50%, transparent 100%);
	background-size: 50% 100%;
	width: auto;
	height: auto;
	grid-row: 1/2;
	grid-column: 1/2;
	border-radius: 4px;
	animation: ${skeletonAnimation} 1.4s ease-in-out infinite;
	@media (prefers-reduced-motion: reduce) {
		animation: none;
	}
	${({ $sx }) => ($sx ? createCSSFromObject($sx) : "")}
`;

export const Skeleton: FC<Props> = ({
	children,
	shape = "rect",
	mode = "auto",
	width: widthProp,
	height: heightProp,
	center,
	busy = false,
	sx,
	sxWrap,
}) => {
	const childRef = useRef<HTMLElement>(null);
	const ref = useRef<HTMLDivElement>(null);
	const style = useCalcRect(shape, widthProp, heightProp);

	if (ref.current && childRef.current && busy && mode === "auto") {
		const r = childRef.current.getBoundingClientRect();
		ref.current.style.height = `${r.height}px`;
	}

	if (!busy && children) {
		return children;
	}

	if (busy && center && mode === "manual") {
		return (
			<Box
				sx={{
					...sxWrap,
					width: "100%",
					height: "100%",
					display: "flex",
					justifyContent: "center",
					alignItems: "center",
				}}>
				<BoxAnimated ref={ref} $sx={{ ...style, ...sx }} />
			</Box>
		);
	}

	if (busy && mode === "manual") {
		return <BoxAnimated ref={ref} $sx={{ ...style, ...sx }} />;
	}

	return (
		<Box
			sx={{
				...sxWrap,
				display: "grid",
				gridTemplateRows: "auto",
				gridTemplateColumns: "auto",
			}}>
			<Box
				ref={childRef}
				style={{ opacity: busy ? 0 : 1 }}
				sx={{
					gridRow: "1/2",
					gridColumn: "1/2",
				}}>
				{children}
			</Box>
			<BoxAnimated
				ref={ref}
				$sx={{ ...style, width: "100%", ...sx }}
				style={{ opacity: busy ? 0.7 : 0 }}
			/>
		</Box>
	);
};

const useCalcRect = (
	shape: Props["shape"],
	width: Props["width"],
	height: Props["height"]
) => {
	const [style, setStyle] = useState<React.CSSProperties>(() => ({}));
	const theme = useTheme() as ITheme;

	useEffect(() => {
		const backgroundColor =
			theme.name === "light" ? "#E2E8F0" : "#334155";
		switch (shape) {
			case "rect": {
				const borderRadius = "4px";
				if (!(width || height)) {
					setStyle(prev => ({
						...prev,
						width: "100%",
						height: "100%",
						borderRadius,
						backgroundColor,
					}));
					return;
				}
				setStyle(prev => ({
					...prev,
					width: getSize(width ?? height),
					height: getSize(height ?? width),
					borderRadius,
					backgroundColor,
				}));
				return;
			}
			case "circle": {
				const borderRadius = "50%";
				if (!(width || height)) {
					setStyle(prev => ({
						...prev,
						width: "100%",
						height: "100%",
						borderRadius,
						backgroundColor,
					}));
					return;
				}
				setStyle(prev => ({
					...prev,
					width: getSize(width ?? height),
					height: getSize(height ?? width),
					borderRadius,
					backgroundColor,
				}));
				return;
			}
			case "line":
			default: {
				const borderRadius = "30px";
				if (!(width || height)) {
					setStyle(prev => ({
						...prev,
						width: "100%",
						height: "100%",
						borderRadius,
						backgroundColor,
					}));
					return;
				}
				setStyle(prev => ({
					...prev,
					width: getSize(width ?? height),
					height: getSize(height ?? width),
					borderRadius: getSize(height ?? width),
					backgroundColor,
				}));
				return;
			}
		}
	}, [shape, width, height, theme.name]);

	return style;
};

function getSize(dimention: Props["width"]): string {
	return typeof dimention === "string" ? dimention : `${dimention}px`;
}
